// "You are fasting" — the cross-group indicator, for any page that is not a
// group's fasting screen.
//
// ⚠ SMALL AND CALM ON PURPOSE. A running fast is a MULTI-DAY state, so this is
// on screen for days at a time. Anything alert-shaped would be wrong within an
// hour of being right: the point is to be findable, not to be noticed. Hence a
// quiet strip with the existing small dial rather than a card of its own — and
// hence no colour beyond what the dial already earns from elapsed time.
//
// It renders NOTHING when there is no running fast. Same rule the sticky panel
// carries: never an empty box.
//
// ⚠ It reads `group.accessible` and branches on it. A `fast_sessions` row
// deliberately outlives its owner leaving the group, but every
// `/groups/:groupId/fasting` route requires MEMBER — so linking without
// checking sends them to a 403. In that state the strip offers the
// account-level end instead, which is the only way out of the lockout and has
// no other UI anywhere.

import { el } from '../dom.js';
import { ApiClientError, endMyFast, fetchMyFasting } from '../api/client.js';
import { fastDialFigure } from './fastDial.js';
import type { MyFastingState } from '@msc/shared';

/** How often the elapsed time is redrawn. The dial derives from Date.now(). */
const TICK_MS = 60_000;

export interface MyFastStripHandle {
  /** Empty until the fetch resolves, and empty forever if nobody is fasting. */
  root: HTMLElement;
  /** Stop the local clock. The caller MUST call this from `onLeave`. */
  stop: () => void;
}

/**
 * Mount the strip into a container the caller owns.
 *
 * ⚠ FETCHES ON ITS OWN, deliberately NOT folded into the host page's
 * `Promise.all`. A rejected fasting read would take the whole home page down
 * with it, and this is the least important thing on that page. Same shape as
 * the fix in `fasting.ts` for the group fetch: a failure costs the indicator
 * and nothing else. A failure is therefore SILENT — there is nothing useful to
 * say to somebody who may well not be fasting at all.
 */
export function myFastStrip(): MyFastStripHandle {
  const root = el('div', {});
  let timer = 0;
  let stopped = false;

  function stop(): void {
    stopped = true;
    if (timer) window.clearInterval(timer);
    timer = 0;
  }

  function paint(state: MyFastingState): void {
    if (stopped) return;
    const { fast, group } = state;
    // `group` is null exactly when `fast` is, but narrow both: the pair is a
    // server promise and this is the one place a broken one would render a
    // strip with no destination.
    if (!fast || !group) {
      stop();
      root.replaceChildren();
      return;
    }

    const dial = fastDialFigure({ session: fast, nowMs: Date.now(), size: 'small' });

    const lines: HTMLElement[] = [
      el('p', { class: 'fast-strip-lead' }, "You're fasting"),
      el('p', { class: 'muted small' }, group.name),
    ];

    if (group.accessible) {
      lines.push(
        el(
          'p',
          { class: 'small' },
          [el('a', { href: `/groups/${group.groupId}/fasting` }, 'Your fast')],
        ),
      );
    } else {
      // The lockout, seen from the inside. They are not a member any more, so
      // the group's own screens will refuse them — saying so is better than a
      // link that 403s, and the end button below is their only way out.
      lines.push(
        el(
          'p',
          { class: 'muted small' },
          'You are no longer in this group, so its fasting page is closed to you. ' +
            'You can still end this fast here.',
        ),
      );
      const problem = el('p', { class: 'form-error', hidden: true });
      const end = el('button', { class: 'btn-link', type: 'button' }, 'End this fast');
      end.addEventListener('click', () => {
        end.setAttribute('disabled', 'disabled');
        problem.hidden = true;
        // 'other' rather than 'planned': nobody chose to stop here, and the end
        // reason is a record somebody may read back later.
        void endMyFast({ reason: 'other' })
          .then((next) => paint(next))
          .catch((err: unknown) => {
            problem.textContent =
              err instanceof ApiClientError ? err.message : 'Could not end it.';
            problem.hidden = false;
            end.removeAttribute('disabled');
          });
      });
      lines.push(end, problem);
    }

    root.replaceChildren(
      el('section', { class: 'card fast-strip' }, [dial, el('div', { class: 'fast-strip-text' }, lines)]),
    );
  }

  void fetchMyFasting()
    .then((state) => {
      if (stopped || !state.fast) return;
      paint(state);
      // Only once there is something to tick. A non-faster pays for no timer.
      timer = window.setInterval(() => {
        // Re-fetching would be wrong here: the elapsed time is derived from
        // `startedAt` and the local clock, so the dial advances with no server
        // round trip at all. See the note at the top of fastDial.ts.
        if (!stopped) paint(state);
      }, TICK_MS);
    })
    .catch(() => {
      // Silent. See the note above.
    });

  return { root, stop };
}
